import React from 'react';
import { Search, Globe, Zap, Activity, LayoutDashboard, Settings as SettingsIcon, Key, ChevronDown } from 'lucide-react';
import { Network } from '../types';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface HeaderProps {
  txHash: string;
  setTxHash: (hash: string) => void;
  network: Network;
  setNetwork: (network: Network) => void;
  apiKey?: string;
  setApiKey?: (key: string) => void;
  provider?: string;
  setProvider?: (provider: string) => void;
  onSearch: () => void;
  isLoading: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  txHash,
  setTxHash,
  network,
  setNetwork,
  apiKey = '',
  setApiKey,
  provider = 'infura',
  setProvider,
  onSearch,
  isLoading
}) => {
  const location = useLocation();
  const [rpcStatus, setRpcStatus] = React.useState<'checking' | 'ok' | 'error'>('checking');
  const [showRpcMenu, setShowRpcMenu] = React.useState(false);

  React.useEffect(() => {
    const checkHealth = async () => {
      try {
        const q = new URLSearchParams();
        q.set('network', network);
        if (apiKey) q.set('apiKey', apiKey);
        if (provider) q.set('provider', provider);
        const res = await fetch(`/api/health?${q.toString()}`);
        const data = await res.json();
        setRpcStatus(data.rpcConnected ? 'ok' : 'error');
      } catch (err) {
        setRpcStatus('error');
      }
    };
    checkHealth();
  }, [network, apiKey, provider]);

  return (
    <header className="min-h-16 border-b border-[#262626] flex flex-col md:flex-row items-center px-4 md:px-6 py-4 md:py-0 gap-4 md:gap-6 glass-panel sticky top-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-md">
      <div className="flex items-center justify-between w-full md:w-auto">
        <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="w-8 h-8 bg-emerald-500 rounded flex items-center justify-center">
            <Zap className="text-black w-5 h-5" />
          </div>
          <h1 className="font-mono font-bold text-lg tracking-tighter text-white uppercase">Oru Trace</h1>
        </Link>
        <div className="md:hidden flex items-center gap-3">
          <div className={cn(
            "w-2 h-2 rounded-full animate-pulse",
            rpcStatus === 'ok' ? "bg-emerald-500" : rpcStatus === 'checking' ? "bg-yellow-500" : "bg-red-500"
          )} />
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#525252]">
            {rpcStatus === 'ok' ? 'OK' : rpcStatus === 'checking' ? '...' : 'ERR'}
          </span>
        </div>
      </div>

      <div className="hidden md:block h-8 w-[1px] bg-[#262626]" />

      <div className="flex-1 flex flex-col sm:flex-row items-center gap-3 w-full">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
          <input
            type="text"
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
            placeholder="Transaction Hash (0x...)"
            className="w-full bg-[#1A1A1A] border border-[#262626] rounded-md h-10 pl-10 pr-4 font-mono text-xs sm:text-sm focus:outline-none focus:border-emerald-500/50 transition-colors text-white"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="flex-1 sm:flex-none flex items-center gap-2 bg-[#1A1A1A] border border-[#262626] rounded-md h-10 px-3">
            <Globe className="w-4 h-4 text-[#525252]" />
            <select
              value={network}
              onChange={(e) => setNetwork(e.target.value as Network)}
              className="bg-transparent text-[10px] sm:text-xs font-mono focus:outline-none cursor-pointer text-[#A3A3A3] w-full"
            >
              <option value="ethereum">Ethereum</option>
              <option value="linea">Linea</option>
              <option value="polygon">Polygon</option>
              <option value="base">Base</option>
              <option value="arbitrum">Arbitrum</option>
              <option value="optimism">Optimism</option>
              <option value="bsc">BSC</option>
              <option value="blast">Blast</option>
              <option value="mantle">Mantle</option>
              <option value="avalanche">Avalanche</option>
              <option value="scroll">Scroll</option>
              <option value="celo">Celo</option>
              <option value="sepolia">Sepolia</option>
            </select>
          </div>

          <button
            onClick={onSearch}
            disabled={isLoading || !txHash}
            className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold px-4 sm:px-6 h-10 rounded-md transition-all text-[10px] uppercase tracking-widest flex items-center gap-2"
          >
            {isLoading ? (
              <Activity className="w-4 h-4 animate-spin" />
            ) : (
              "TRACE"
            )}
          </button>
        </div>
      </div>

      <div className="hidden lg:flex items-center gap-2 text-xs font-mono text-[#525252]">
        <Link 
          to="/app/dashboard" 
          className={cn(
            "p-2 hover:bg-[#1A1A1A] rounded-md transition-colors",
            location.pathname === '/app/dashboard' ? "text-emerald-500 bg-emerald-500/5" : "text-[#525252]"
          )} 
          title="Dashboard"
        >
          <LayoutDashboard className="w-4 h-4" />
        </Link>
        <Link 
          to="/app/settings" 
          className={cn(
            "p-2 hover:bg-[#1A1A1A] rounded-md transition-colors",
            location.pathname === '/app/settings' ? "text-emerald-500 bg-emerald-500/5" : "text-[#525252]"
          )} 
          title="Settings"
        >
          <SettingsIcon className="w-4 h-4" />
        </Link>
        <div className="relative">
          <button 
            onClick={() => setShowRpcMenu(!showRpcMenu)}
            className={cn(
              "flex items-center gap-2 p-2 px-3 rounded-md transition-all border",
              showRpcMenu ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-500" : "hover:bg-[#1A1A1A] border-transparent text-[#525252]"
            )} 
            title="RPC Configuration"
          >
            <Key className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-widest">RPC</span>
            <ChevronDown className={cn("w-3 h-3 transition-transform", showRpcMenu && "rotate-180")} />
          </button>

          <AnimatePresence>
            {showRpcMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute right-0 top-full mt-2 w-72 bg-[#1A1A1A] border border-[#333] rounded-lg shadow-2xl p-4 z-[100] backdrop-blur-xl"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-[#333] pb-2 mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">Trace Access</span>
                    <div className={cn(
                      "w-1.5 h-1.5 rounded-full",
                      rpcStatus === 'ok' ? "bg-emerald-500" : "bg-red-500"
                    )} />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-[#525252] ml-1">Provider</label>
                    <select
                      value={provider}
                      onChange={(e) => setProvider?.(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-emerald-500/50 transition-all"
                    >
                      <option value="infura">Infura</option>
                      <option value="alchemy">Alchemy</option>
                      <option value="custom">Custom URL</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-[#525252] ml-1">
                      {provider === 'custom' ? 'Direct URL' : 'API Key'}
                    </label>
                    <input
                      type="password"
                      placeholder="Enter credentials..."
                      value={apiKey}
                      onChange={(e) => setApiKey?.(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-[#333] rounded-md px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-emerald-500/50 transition-all placeholder:text-[#333]"
                    />
                  </div>
                  <p className="text-[8px] text-[#404040] leading-relaxed uppercase">
                    Your local key will be used for execution. If empty, the system reverts to pre-configured defaults.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="h-4 w-[1px] bg-[#262626] mx-2" />
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-2 h-2 rounded-full animate-pulse",
            rpcStatus === 'ok' ? "bg-emerald-500" : rpcStatus === 'checking' ? "bg-yellow-500" : "bg-red-500"
          )} />
          <span className="text-[10px] uppercase tracking-widest">
            RPC: {rpcStatus === 'ok' ? 'OK' : rpcStatus === 'checking' ? '...' : 'ERROR'}
          </span>
        </div>
      </div>
    </header>
  );
};
